import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Mail, Phone, CheckCircle, AlertCircle, Loader2, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerificationModal = ({ isOpen, onClose, onVerificationComplete }) => {
  const [step, setStep] = useState(1); // 1 = email, 2 = phone
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [emailOTP, setEmailOTP] = useState('');
  const [phoneOTP, setPhoneOTP] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [demoOTP, setDemoOTP] = useState(''); // For demo display

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setStep(1);
      setEmailOTP('');
      setPhoneOTP('');
      setError('');
      setSuccess('');
      setOtpSent(false);
      setDemoOTP('');
      
      // Fetch user's email
      const fetchUserEmail = async () => {
        try {
          const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setEmail(res.data.email);
        } catch (e) {
          console.error('Error fetching user:', e);
        }
      };
      fetchUserEmail();
    }
  }, [isOpen]);

  const handleSendOTP = async (type) => {
    const contact = type === 'email' ? email : phone;
    
    if (!contact) {
      setError(`Please enter your ${type}`);
      return;
    }

    if (type === 'phone' && !/^\+?[1-9]\d{1,14}$/.test(phone.replace(/\s/g, ''))) {
      setError('Please enter a valid phone number (e.g., +27 12 345 6789)');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await axios.post(
        `${API}/verification/send-otp`,
        { contact, type },
        { withCredentials: true }
      );
      
      setOtpSent(true);
      setDemoOTP(res.data.otp_code); // For demo purposes
      setSuccess(`OTP sent to ${contact}`);
    } catch (e) {
      setError(e.response?.data?.detail || `Failed to send OTP to ${type}`);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (type) => {
    const contact = type === 'email' ? email : phone;
    const otpCode = type === 'email' ? emailOTP : phoneOTP;

    if (!otpCode || otpCode.length !== 6) {
      setError('Please enter the 6-digit OTP code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(
        `${API}/verification/verify-otp`,
        { contact, otp_code: otpCode, type },
        { withCredentials: true }
      );

      setSuccess(`${type === 'email' ? 'Email' : 'Phone'} verified successfully!`);
      
      if (type === 'email') {
        // Move to phone verification
        setTimeout(() => {
          setStep(2);
          setError('');
          setSuccess('');
          setOtpSent(false);
          setDemoOTP('');
        }, 1500);
      } else {
        // Both verifications complete
        setTimeout(() => {
          onVerificationComplete();
          onClose();
        }, 1500);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Invalid OTP code');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#37b34a] to-[#2d9a3e] text-white p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <Shield size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Verification Required</h2>
              <p className="text-sm text-white/90">Step {step} of 2</p>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <div className={`flex-1 h-1 rounded-full ${step >= 1 ? 'bg-white' : 'bg-white/30'}`} />
            <div className={`flex-1 h-1 rounded-full ${step >= 2 ? 'bg-white' : 'bg-white/30'}`} />
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 1 ? (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                  <Mail size={20} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Email Verification</h3>
                  <p className="text-sm text-gray-600">Verify your email address</p>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  value={email}
                  disabled
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                />
              </div>

              {!otpSent ? (
                <button
                  onClick={() => handleSendOTP('email')}
                  disabled={loading}
                  className="w-full bg-[#37b34a] hover:bg-[#2d9a3e] disabled:bg-gray-300 text-white font-semibold px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <Mail size={18} />}
                  Send OTP to Email
                </button>
              ) : (
                <>
                  {demoOTP && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-xs font-semibold text-amber-800 mb-1">🎭 DEMO MODE</p>
                      <p className="text-sm text-amber-700">Your OTP code: <span className="font-bold text-lg">{demoOTP}</span></p>
                    </div>
                  )}
                  
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Enter OTP Code</label>
                    <input
                      type="text"
                      value={emailOTP}
                      onChange={(e) => setEmailOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-2xl font-bold tracking-widest"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setOtpSent(false)}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-6 py-3 rounded-lg transition-colors"
                    >
                      Resend OTP
                    </button>
                    <button
                      onClick={() => handleVerifyOTP('email')}
                      disabled={loading || emailOTP.length !== 6}
                      className="flex-1 bg-[#37b34a] hover:bg-[#2d9a3e] disabled:bg-gray-300 text-white font-semibold px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {loading ? <Loader2 size={18} className="animate-spin" /> : 'Verify'}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                  <Phone size={20} className="text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Phone Verification</h3>
                  <p className="text-sm text-gray-600">Verify your phone number</p>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+27 12 345 6789"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                  disabled={otpSent}
                />
              </div>

              {!otpSent ? (
                <button
                  onClick={() => handleSendOTP('phone')}
                  disabled={loading}
                  className="w-full bg-[#37b34a] hover:bg-[#2d9a3e] disabled:bg-gray-300 text-white font-semibold px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <Phone size={18} />}
                  Send OTP to Phone
                </button>
              ) : (
                <>
                  {demoOTP && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-xs font-semibold text-amber-800 mb-1">🎭 DEMO MODE</p>
                      <p className="text-sm text-amber-700">Your OTP code: <span className="font-bold text-lg">{demoOTP}</span></p>
                    </div>
                  )}
                  
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Enter OTP Code</label>
                    <input
                      type="text"
                      value={phoneOTP}
                      onChange={(e) => setPhoneOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-2xl font-bold tracking-widest"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setOtpSent(false)}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-6 py-3 rounded-lg transition-colors"
                    >
                      Resend OTP
                    </button>
                    <button
                      onClick={() => handleVerifyOTP('phone')}
                      disabled={loading || phoneOTP.length !== 6}
                      className="flex-1 bg-[#37b34a] hover:bg-[#2d9a3e] disabled:bg-gray-300 text-white font-semibold px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {loading ? <Loader2 size={18} className="animate-spin" /> : 'Verify'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Messages */}
          {error && (
            <div className="mt-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-4 py-3 rounded-lg">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {success && (
            <div className="mt-4 flex items-center gap-2 text-sm text-green-600 bg-green-50 px-4 py-3 rounded-lg">
              <CheckCircle size={16} />
              {success}
            </div>
          )}

          {/* Info */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-700">
              <strong>Note:</strong> OTP codes expire in 10 minutes. You'll receive a 6-digit code {step === 1 ? 'via email' : 'via SMS'}.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationModal;
